import { useState, useEffect } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Link } from 'react-router-dom';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ years: 0, customers: 0, repairs: 0, rating: 0 });

  useEffect(() => {
    setIsVisible(true);
    
    // Animate counters
    const animateCounters = () => {
      const targets = { years: 15, customers: 5000, repairs: 25000, rating: 4.9 };
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setCounters({
          years: Math.floor(targets.years * progress),
          customers: Math.floor(targets.customers * progress),
          repairs: Math.floor(targets.repairs * progress),
          rating: Math.min(targets.rating, (targets.rating * progress).toFixed(1))
        });
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepDuration);
    };
    
    const timer = setTimeout(animateCounters, 500);
    return () => clearTimeout(timer);
  }, []);

  const team = [
    {
      name: 'David Johnson',
      position: 'Master Technician & Owner',
      experience: '15+ Years Experience',
      specialties: ['Engine Diagnostics', 'Transmission Repair', 'Electrical Systems'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20automotive%20master%20technician%20in%20clean%20work%20uniform%20standing%20confidently%20in%20modern%20garage%20workshop%20with%20tools%20and%20equipment%20in%20background%2C%20realistic%20portrait%20photography&width=400&height=500&seq=1&orientation=portrait'
    },
    {
      name: 'Tommy Rodriguez',
      position: 'Senior Technician',
      experience: '12+ Years Experience',
      specialties: ['Brake Systems', 'Suspension', 'AC & Heating'],
      image: 'https://readdy.ai/api/search-image?query=Experienced%20automotive%20technician%20in%20professional%20work%20attire%20standing%20in%20clean%20modern%20auto%20repair%20shop%20with%20diagnostic%20equipment%20visible%20in%20background%2C%20realistic%20portrait&width=400&height=500&seq=2&orientation=portrait'
    },
    {
      name: 'Jake Thompson',
      position: 'Diagnostic Specialist',
      experience: '8+ Years Experience',
      specialties: ['Computer Diagnostics', 'Fuel Systems', 'Emissions'],
      image: 'https://readdy.ai/api/search-image?query=Young%20professional%20automotive%20diagnostic%20specialist%20in%20work%20uniform%20standing%20next%20to%20modern%20diagnostic%20computer%20equipment%20in%20clean%20garage%20workshop%2C%20realistic%20portrait%20photography&width=400&height=500&seq=3&orientation=portrait'
    }
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Workmanship',
      description: 'We take pride in delivering exceptional quality repairs that last, using only the best parts and proven techniques.'
    },
    {
      icon: 'ri-heart-line',
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We listen to your concerns and provide honest, transparent service every time.'
    },
    {
      icon: 'ri-time-line',
      title: 'Reliable Service',
      description: 'We respect your time and schedule. Our efficient service gets you back on the road quickly and safely.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Fair Pricing',
      description: 'Honest, upfront pricing with no hidden fees. We provide detailed estimates and explain all costs clearly.'
    }
  ];

  const teamMembers = [
    {
      name: 'David Thompson',
      role: 'Lead EV Technician',
      experience: '12 years',
      certifications: 'Tesla Certified, ASE Master EV Tech',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20EV%20technician%20in%20clean%20work%20uniform%20standing%20confidently%20in%20modern%20electric%20vehicle%20repair%20shop%20with%20EV%20charging%20stations%20and%20diagnostic%20equipment%20in%20background&width=300&height=400&seq=1&orientation=portrait',
      bio: 'Specialized in Tesla and luxury EV repairs with extensive high-voltage system experience.'
    },
    {
      name: 'Jennifer Martinez',
      role: 'EV Battery Specialist',
      experience: '8 years',
      certifications: 'Nissan EV Certified, BMW i-Series Tech',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20EV%20technician%20in%20clean%20work%20uniform%20standing%20confidently%20in%20modern%20electric%20vehicle%20repair%20shop%20with%20battery%20diagnostic%20equipment%20and%20tools%20in%20background&width=300&height=400&seq=2&orientation=portrait',
      bio: 'Expert in battery diagnostics, thermal management, and EV charging system repairs.'
    },
    {
      name: 'Michael Chen',
      role: 'EV Software Specialist',
      experience: '6 years',
      certifications: 'Ford EV Certified, VW ID Specialist',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20EV%20technician%20in%20clean%20work%20uniform%20standing%20confidently%20in%20modern%20electric%20vehicle%20repair%20shop%20with%20computer%20diagnostic%20equipment%20and%20software%20tools%20in%20background&width=300&height=400&seq=3&orientation=portrait',
      bio: 'Focuses on EV software updates, programming, and advanced diagnostic systems.'
    }
  ];

  const certifications = [
    { name: 'ASE Master EV Technician', icon: 'ri-award-line' },
    { name: 'Tesla Service Certified', icon: 'ri-flashlight-line' },
    { name: 'Nissan EV Specialist', icon: 'ri-leaf-line' },
    { name: 'BMW i-Series Certified', icon: 'ri-car-line' },
    { name: 'High Voltage Safety Certified', icon: 'ri-shield-check-line' },
    { name: 'Ford EV Technician', icon: 'ri-tools-line' }
  ];

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About DJT EV Mechanic
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Leading the future of electric vehicle service with expertise, innovation, and commitment to sustainable transportation.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-6">
                Our EV Story
              </h2>
              <div className="space-y-4 text-black">
                <p>
                  Founded in 2014, DJT EV Mechanic began as a forward-thinking automotive shop that recognized 
                  the electric vehicle revolution early. As EVs started gaining popularity, we invested heavily 
                  in specialized training, equipment, and certifications to become the region's premier EV service center.
                </p>
                <p>
                  Today, we're proud to be certified technicians for major EV brands including Tesla, Nissan, 
                  BMW, Ford, and many others. Our team has completed over 5,000 EV repairs and maintenance services, 
                  making us the trusted choice for electric vehicle owners.
                </p>
                <p>
                  We believe in the future of sustainable transportation and are committed to keeping electric 
                  vehicles running efficiently, safely, and reliably. Our mission is to support the EV community 
                  with expert service and honest, transparent pricing.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20electric%20vehicle%20repair%20shop%20interior%20showing%20clean%20organized%20workspace%20with%20EV%20charging%20stations%2C%20advanced%20diagnostic%20equipment%2C%20electric%20car%20lifts%2C%20and%20professional%20technicians%20working%20on%20electric%20vehicles&width=600&height=500&seq=4&orientation=landscape"
                alt="DJT EV Mechanic shop interior"
                className="w-full h-auto rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Our Expert EV Team
            </h2>
            <p className="text-xl text-black">
              Meet our certified electric vehicle technicians
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-[#f5f1e8] border border-green-200 rounded-lg shadow-lg overflow-hidden">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-green-600 mb-2">{member.name}</h3>
                  <p className="text-green-500 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-black mb-2">
                    <strong>Experience:</strong> {member.experience}
                  </p>
                  <p className="text-sm text-black mb-3">
                    <strong>Certifications:</strong> {member.certifications}
                  </p>
                  <p className="text-black text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Our EV Certifications
            </h2>
            <p className="text-xl text-black">
              Industry-leading certifications and training
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-[#f5f1e8] border border-green-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className={`${cert.icon} text-3xl text-green-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-black">{cert.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Our Values
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-leaf-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Sustainability</h3>
              <p className="text-black">Supporting clean transportation and environmental responsibility</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Safety</h3>
              <p className="text-black">Highest safety standards for high-voltage EV systems</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-award-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Excellence</h3>
              <p className="text-black">Continuous training and certification in latest EV technology</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-heart-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Community</h3>
              <p className="text-black">Supporting the growing EV community with expert service</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Experience the DJT EV Difference
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied EV owners who trust us with their electric vehicles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/appointment" 
              className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book EV Service
            </Link>
            <a 
              href="tel:18005700229" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-green-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call 1-800-570-0229
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}